<template>
  <nav class="navbar" :class="{ scrolled: isScrolled }">
    <div class="nav-inner container">
      <RouterLink to="/" class="nav-logo">
        <!--
          Left arm: (4,4)→(26,42). Perpendicular right unit = (0.866, -0.501)
          Offset +2 (inner/right side): (5.73, 3.0)→(27.73, 41)
          Offset -2 (outer/left side): (2.27, 5.0)→(24.27, 43)

          Right arm: (48,4)→(26,42). Perpendicular right unit = (0.866, 0.501)
          Offset +2 (outer/right side): (49.73, 5.0)→(27.73, 43)
          Offset -2 (inner/left side): (46.27, 3.0)→(24.27, 41)
        -->
        <svg class="logo-mark" width="66" height="50" viewBox="-12 0 72 50" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <!-- Primary shade — vivid blue→purple -->
            <linearGradient id="vg1" x1="4" y1="0" x2="48" y2="0" gradientUnits="userSpaceOnUse">
              <stop offset="0%"   stop-color="#5b8fff"/>
              <stop offset="100%" stop-color="#a78bfa"/>
            </linearGradient>
            <!-- Secondary shade — lighter blue→purple (the "double" line) -->
            <linearGradient id="vg2" x1="4" y1="0" x2="48" y2="0" gradientUnits="userSpaceOnUse">
              <stop offset="0%"   stop-color="#a0c8ff"/>
              <stop offset="100%" stop-color="#d4b8ff"/>
            </linearGradient>
          </defs>

          <!-- Terminal dots -->
          <circle cx="4"  cy="4" r="3" fill="#5b8fff"/>
          <circle cx="48" cy="4" r="3" fill="#a78bfa"/>

          <!-- LEFT ARM — two parallel lines in two shades -->
          <!-- Outer line (left/outer side of arm) — lighter shade -->
          <line x1="2.3"  y1="5"  x2="24.3" y2="43"
                stroke="url(#vg2)" stroke-width="2.2" stroke-linecap="round"/>
          <!-- Inner line (right/inner side of arm) — vivid shade -->
          <line x1="5.7"  y1="3"  x2="27.7" y2="41"
                stroke="url(#vg1)" stroke-width="2.2" stroke-linecap="round"/>

          <!-- RIGHT ARM — two parallel lines in two shades -->
          <!-- Inner line (left/inner side of arm) — vivid shade -->
          <line x1="46.3" y1="3"  x2="24.3" y2="41"
                stroke="url(#vg1)" stroke-width="2.2" stroke-linecap="round"/>
          <!-- Outer line (right/outer side of arm) — lighter shade -->
          <line x1="49.7" y1="5"  x2="27.7" y2="43"
                stroke="url(#vg2)" stroke-width="2.2" stroke-linecap="round"/>

          <!-- </> code tag on the left / arm -->
          <text x="-9" y="24"
                font-family="monospace" font-size="9" font-weight="700"
                fill="#5b8fff" opacity="0.9">&lt;/&gt;</text>

          <!-- ORBIT on right arm at ~28% down → (41.8, 14.6) -->
          <circle cx="41.8" cy="14.6" r="8"
                  fill="none" stroke="#a78bfa" stroke-width="1.1"
                  stroke-dasharray="2.4 3" opacity="0.6"/>
          <ellipse cx="41.8" cy="14.6" rx="8" ry="3.2"
                   fill="none" stroke="#a78bfa" stroke-width="0.7"
                   stroke-dasharray="1.5 2.5" opacity="0.25"/>

          <!-- Airplane orbiting clockwise -->
          <g>
            <path d="M49.8,18.2 L52.8,11.2 L49.8,13 L46.8,11.2 Z" fill="#e8b84b"/>
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 41.8 14.6"
              to="360 41.8 14.6"
              dur="4.5s"
              repeatCount="indefinite"/>
          </g>
        </svg>
        <span class="logo-text">vidya</span>
      </RouterLink>

      <ul class="nav-links" :class="{ open: mobileOpen }">
        <li><RouterLink to="/" @click="close">Home</RouterLink></li>
        <li><RouterLink to="/about" @click="close">About</RouterLink></li>
        <li><RouterLink to="/projects" @click="close">Projects</RouterLink></li>
      </ul>

      <div class="nav-right">
        <button
          class="theme-toggle"
          @click="handleThemeToggle"
          :title="isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'"
        >
          <span>{{ isDarkMode ? '☀️' : '🌙' }}</span>
        </button>
        <button
          class="burger"
          :class="{ open: mobileOpen }"
          @click="mobileOpen = !mobileOpen"
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useMainStore } from '@/stores/main'

const store = useMainStore()
const { isDarkMode } = storeToRefs(store)
const { toggleDarkMode } = store

const mobileOpen = ref(false)
const isScrolled = ref(false)

function close() {
  mobileOpen.value = false
}

function handleThemeToggle() {
  toggleDarkMode()
  close()
}

function handleScroll() {
  isScrolled.value = window.scrollY > 60
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped>
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 20px 0;
  transition: all var(--transition-normal);
}

.navbar.scrolled {
  padding: 12px 0;
  background: rgba(8, 12, 20, 0.88);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--border);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.3);
}

[data-theme="light"] .navbar.scrolled {
  background: rgba(240, 242, 250, 0.9);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.nav-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.nav-logo {
  display: flex;
  align-items: center;
  gap: 9px;
  transition: opacity var(--transition-fast);
}

.nav-logo:hover {
  opacity: 0.82;
  color: var(--text);
}

.logo-mark {
  flex-shrink: 0;
  height: 42px;
  width: auto;
  overflow: visible;
  transition: transform var(--transition-fast);
}


.nav-logo:hover .logo-mark {
  transform: translateY(-2px);
}

.logo-text {
  font-family: var(--font-display);
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: var(--text);
  line-height: 1;
}

.nav-links {
  display: flex;
  list-style: none;
  gap: var(--space-8);
  margin: 0;
  padding: 0;
}

.nav-links a {
  font-family: var(--font-display);
  font-size: 0.88rem;
  font-weight: 500;
  color: var(--text-muted);
  letter-spacing: 0.3px;
  transition: color var(--transition-fast);
  position: relative;
  padding-bottom: 4px;
}

.nav-links a::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 1.5px;
  background: linear-gradient(90deg, var(--primary), var(--purple));
  border-radius: 2px;
  transform: scaleX(0);
  transform-origin: left;
  transition: transform var(--transition-normal);
}

.nav-links a:hover,
.nav-links a.router-link-active {
  color: var(--text);
}

.nav-links a.router-link-active::after,
.nav-links a:hover::after {
  transform: scaleX(1);
}

.nav-right {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.theme-toggle {
  background: none;
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1rem;
  transition: all var(--transition-fast);
  color: var(--text-muted);
}

.theme-toggle:hover {
  border-color: var(--primary);
  background: var(--primary-dim);
  transform: scale(1.05);
}

.burger {
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: none;
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  width: 38px;
  height: 38px;
  cursor: pointer;
  padding: 9px;
  gap: 4px;
}

.burger span {
  display: block;
  width: 100%;
  height: 2px;
  background: var(--text);
  border-radius: 2px;
  transition: all var(--transition-fast);
}

.burger.open span:nth-child(1) {
  transform: rotate(45deg) translate(4px, 4px);
}
.burger.open span:nth-child(2) {
  opacity: 0;
  transform: scaleX(0);
}
.burger.open span:nth-child(3) {
  transform: rotate(-45deg) translate(4px, -4px);
}

@media (max-width: 768px) {
  .burger {
    display: flex;
  }

  .nav-links {
    position: fixed;
    inset: 0;
    background: var(--bg);
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--space-10);
    transform: translateX(100%);
    transition: transform var(--transition-normal);
    z-index: 999;
  }

  .nav-links.open {
    transform: translateX(0);
  }

  .nav-links a {
    font-size: 1.6rem;
    font-weight: 600;
  }
}
</style>
